export interface DashboardDataset {
    id: string;
    title: string;
    description?: string | null;
    connector: string;
    tool: string;
    args: Record<string, unknown>;
    variables: string[];
}

export interface DashboardSummary {
    id: string;
    title: string;
    createdAt: string;
    updatedAt: string;
    sharedAt?: string | null;
    visibleInLists?: boolean;
}

export interface SharedDashboardSummary extends DashboardSummary {
    ownerIdentifier: string;
    hasAccess: boolean;
}

export interface Dashboard extends DashboardSummary {
    html: string;
    datasets: DashboardDataset[];
    variables: Record<string, { default?: unknown }>;
    isOwner: boolean;
}

export class DashboardRequestError extends Error {
    constructor(message: string, readonly status: number) {
        super(message);
    }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
    const response = await fetch(path, { credentials: 'include', ...init });
    if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new DashboardRequestError(
            typeof body?.detail === 'string'
                ? body.detail
                : response.statusText,
            response.status
        );
    }
    return response.json();
}

const dashboardPath = (id: string) =>
    `/api/dashboards/${encodeURIComponent(id)}`;

export const listDashboards = () =>
    request<DashboardSummary[]>('/api/dashboards');

export const getDashboard = (id: string, published = false) =>
    request<Dashboard>(
        `${dashboardPath(id)}${published ? '?published=true' : ''}`
    );

export const getDashboardPreview = (id: string) =>
    request<{ html: string }>(`${dashboardPath(id)}/preview`);

export const deleteDashboard = (id: string) =>
    request<{ success: boolean }>(dashboardPath(id), { method: 'DELETE' });

export const SHARED_DASHBOARDS_KEY = 'shared-dashboards';

export const shareDashboard = (id: string, shared: boolean) =>
    request<DashboardSummary>(`${dashboardPath(id)}/share`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shared })
    });

export interface DashboardAccessInput {
    userIds: string[];
    groupIds: string[];
    organization?: boolean;
}

export const exportDashboardAsApp = (
    id: string,
    name: string,
    grouping: string,
    access?: DashboardAccessInput
) =>
    request<{ success: boolean; shared: boolean; version: number }>(
        `${dashboardPath(id)}/export-app`,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name, grouping, access })
        }
    );

export interface DashboardExportState {
    version: number;
    publishedAt?: string | null;
    name?: string | null;
    grouping?: string | null;
}

export const getDashboardExportState = (id: string) =>
    request<DashboardExportState>(`${dashboardPath(id)}/export-state`);

export const listAppGroups = () =>
    request<{ groups: string[] }>('/api/dashboards/export-app/groups').then(
        (response) => response.groups
    );

export const setDashboardListing = (id: string, visible: boolean) =>
    request<DashboardSummary>(`${dashboardPath(id)}/listing`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visible })
    });

export interface AccessUser {
    userId: string;
    displayName: string;
    email: string;
}

export interface AccessGroup {
    groupId: string;
    groupName: string;
}

export interface DashboardAccess {
    users: AccessUser[];
    groups: AccessGroup[];
    sharedAt: string | null;
    organization: boolean;
}

export const getDashboardAccess = (id: string) =>
    request<DashboardAccess>(`${dashboardPath(id)}/access`);

export const setDashboardAccess = (
    id: string,
    access: { userIds: string[]; groupIds: string[]; organization?: boolean }
) =>
    request<DashboardAccess>(`${dashboardPath(id)}/access`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(access)
    });

const directoryQuery = (
    search: string,
    page: number,
    size: number,
    dashboardId: string,
    filter: 'All' | 'Yes' | 'No'
) =>
    new URLSearchParams({
        search,
        page: String(page),
        size: String(size),
        dashboardId,
        filter
    }).toString();

export const listAccessUsers = (
    search: string,
    page: number,
    size: number,
    dashboardId: string,
    filter: 'All' | 'Yes' | 'No' = 'All'
) =>
    request<{ data: AccessUser[]; rows: number }>(
        `/api/dashboards/access/users?${directoryQuery(
            search,
            page,
            size,
            dashboardId,
            filter
        )}`
    );

export const listAccessGroups = (
    search: string,
    page: number,
    size: number,
    dashboardId: string,
    filter: 'All' | 'Yes' | 'No' = 'All'
) =>
    request<{ data: AccessGroup[]; rows: number }>(
        `/api/dashboards/access/groups?${directoryQuery(
            search,
            page,
            size,
            dashboardId,
            filter
        )}`
    );

export const listSharedDashboards = () =>
    request<SharedDashboardSummary[]>('/api/dashboards/shared');

export const runDashboardDataset = (
    id: string,
    datasetId: string,
    published = false,
    variables: Record<string, unknown> = {}
) =>
    request<{ data: unknown }>(
        `${dashboardPath(id)}/datasets/${encodeURIComponent(datasetId)}/run${
            published ? '?published=true' : ''
        }`,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ variables })
        }
    );

export const renameDashboard = (dashboard: Dashboard, title: string) =>
    request<Dashboard>(dashboardPath(dashboard.id), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            title,
            html: dashboard.html,
            datasets: dashboard.datasets,
            variables: dashboard.variables
        })
    });
